import { Affiliate } from 'lib/context/AffiliateContext';
import { getCookie } from 'lib/utils/client-storage-utils';

const getRbaSource = (defaultRbaSource?: string) => {
  if (typeof window === 'undefined') {
    return defaultRbaSource ?? '';
  }

  const qs = new URLSearchParams(window.location.search);
  const fromQuery = qs.get('rba_source') || qs.get('rbaSource');
  const fromCookie =
    (getCookie('rba_source') as string) || (getCookie('rbaSource') as string) || '';

  if (fromQuery && fromQuery.length) {
    return fromQuery;
  }
  if (fromCookie && fromCookie.length) {
    return fromCookie;
  }
  if (defaultRbaSource && defaultRbaSource.trim().length) {
    return defaultRbaSource;
  }

  return 'Organic Web - Appt Request - Corporate Direct';
};

const getRbaBreakdown = (defaultRbaBreakdown?: string) => {
  if (typeof window === 'undefined') {
    return defaultRbaBreakdown ?? '';
  }

  const qs = new URLSearchParams(window.location.search);
  const fromQuery = qs.get('rba_breakdown') || qs.get('rbaBreakdown');
  const fromCookie =
    (getCookie('rba_breakdown') as string) || (getCookie('rbaBreakdown') as string) || '';

  if (fromQuery && fromQuery.length) {
    return fromQuery;
  }
  if (fromCookie && fromCookie.length) {
    return fromCookie;
  }
  if (defaultRbaBreakdown && defaultRbaBreakdown.trim().length) {
    return defaultRbaBreakdown;
  }

  return '';
};

const getUserLocation = () => {
  const zip = (getCookie('currentZip') as string) || '';
  const city = (getCookie('currentCity') as string) || '';
  const state = (getCookie('currentState') as string) || '';

  return { zip, city, state };
};

const buildFieldsJson = (
  rawFieldsJson: string,
  affiliate: Affiliate,
  defaultRbaSource?: string,
  defaultRbaBreakdown?: string
) => {
  if (!rawFieldsJson) {
    return rawFieldsJson;
  }

  let parsed: any;
  try {
    parsed = JSON.parse(rawFieldsJson);
  } catch {
    return rawFieldsJson;
  }

  const rbaSource = getRbaSource(defaultRbaSource);
  const rbaBreakdown = getRbaBreakdown(defaultRbaBreakdown);

  if (parsed.RbA_Source) {
    parsed.RbA_Source.value = rbaSource ?? '';
  }

  if (parsed.RbA_Breakdown) {
    parsed.RbA_Breakdown.value = rbaBreakdown ?? '';
  }

  if (parsed.affiliateID && affiliate?.affiliateId) {
    parsed.affiliateID.value = affiliate.affiliateId ?? '';
  }

  if (parsed.affiliatename && affiliate?.affiliateName) {
    parsed.affiliatename.value = affiliate.affiliateName ?? '';
  }

  if (parsed.affiliatelocation) {
    parsed.affiliatelocation.value =
      affiliate?.city && affiliate?.state?.abbreviation
        ? `${affiliate.city}, ${affiliate.state.abbreviation}`
        : parsed.affiliatelocation.value;
  }

  const userLoc = getUserLocation();

  if (parsed.userZip && userLoc.zip) {
    parsed.userZip.value = userLoc.zip;
  }
  if (parsed.userCity && userLoc.city) {
    parsed.userCity.value = userLoc.city;
  }
  if (parsed.userState && userLoc.state) {
    parsed.userState.value = userLoc.state;
  }

  return JSON.stringify(parsed);
};

export const buildChatPropertyKeys = (
  propertyKeys: string,
  affiliate: Affiliate,
  defaultRbaSource?: string,
  defaultRbaBreakdown?: string
): string => {
  if (!propertyKeys) {
    return '';
  }

  const match = propertyKeys.match(/(^|&)fields=([^&]*)/);
  if (!match) {
    return propertyKeys;
  }

  const encodedFields = match[2];
  let rawFieldsJson = '';

  try {
    rawFieldsJson = decodeURIComponent(encodedFields);
  } catch {
    return propertyKeys;
  }

  const updatedFieldsJson = buildFieldsJson(
    rawFieldsJson,
    affiliate,
    defaultRbaSource,
    defaultRbaBreakdown
  );

  const newEncodedFields = encodeURIComponent(updatedFieldsJson);

  const updatedPropertyKeys = propertyKeys.replace(/fields=[^&]*/, `fields=${newEncodedFields}`);

  return updatedPropertyKeys;
};
